import React, { useState, useEffect, useRef } from 'react';

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
  allowCustomInput?: boolean; // Nueva prop para permitir escritura libre
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({ 
  value, 
  onChange, 
  options, 
  placeholder,
  disabled = false,
  allowCustomInput = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  const filteredOptions = allowCustomInput 
    ? options.filter(option => option.toLowerCase().includes(searchTerm.toLowerCase()))
    : options; // Para campos de solo selección, siempre mostrar todas las opciones

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Para campos de solo selección, mantener el valor actual visible
        if (!allowCustomInput) {
          setSearchTerm(value);
        } else {
          setSearchTerm(value); // Reset to current value for custom input fields
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [value, allowCustomInput]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (allowCustomInput) {
      setSearchTerm(e.target.value);
      onChange(e.target.value);
      setIsOpen(true);
      setHighlightedIndex(-1);
    }
    // Si allowCustomInput es false, no se permite escribir ni cambiar el searchTerm
  };

  const handleOptionClick = (option: string) => {
    onChange(option);
    setSearchTerm(option);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleOptionClick(filteredOptions[highlightedIndex]);
      } else if (allowCustomInput && searchTerm.trim() !== '') {
        onChange(searchTerm);
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm(value);
    } else if (!allowCustomInput) {
      // Si no se permite escritura libre, prevenir la entrada de texto
      e.preventDefault();
    }
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      // Para campos de solo selección, mantener el valor actual visible
      if (!allowCustomInput) {
        setSearchTerm(value);
      }
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%' }} ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        className="form-input"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={!allowCustomInput}
        style={{
          cursor: disabled ? 'not-allowed' : (allowCustomInput ? 'text' : 'pointer'),
          opacity: disabled ? 0.6 : 1
        }}
      />
      {isOpen && !disabled && filteredOptions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '0 0 6px 6px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={option}
              onClick={() => handleOptionClick(option)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: index === highlightedIndex ? '#f1f5f9' : 'transparent',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '13px',
                color: '#374151',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
